/**
 * A group's list formula in the Flow Optimizer's window (user, 12/10/2026):
 * View tabs Formula (what a good list is) · Rules (Exclude only — "Not
 * enough data" by default), and the group's queue ranked live on the right,
 * in the order the day will walk it. The group link's own setting — it never
 * comes from the master config.
 * For Media Devoted | Created By Ciro Autiero
 */
import { trackerFromEnum, trackerLabel } from '@trafficpulse/shared';
import { FC } from 'react';
import { ListChecks, Sigma } from 'lucide-react';
import type { DropGenListRanking, DropGenListRankingPreview } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/api-error';

import { GroupChip } from '../../../shared/GroupChip';
import { HourFormulaCard } from '../../general/hour/formula';
import { DialogSubject } from '../../optimizer-dialog/DialogSubject';
import { OptimizerBody, OptimizerDialog } from '../../optimizer-dialog/OptimizerDialog';
import { RulesCard } from '../../rules/RulesCard';
import type { ContainerEditor } from '../../state/useContainerEditor';
import type { QueueRow } from '../queue/QueueList';
import { ListFormulaResults } from './live';
import type { ListRankingQuery } from './use-list-ranking';

const HINT =
  "Like the Flow Optimizer: each date range has its own formula over the tracker's numbers for each list (grouped by the list the drop was sent to). Rates (CR, EPV, ROI…) compare lists fairly; counts mostly favour the list sent to most. The “Not enough data” rule (Rules) reads the first range.";

/** The ranking's state in one line. */
export function rankingStatus(query: ListRankingQuery): string {
  const data = query.data;
  if (query.isError) return `Ranking unavailable — ${getApiErrorMessage(query.error)}. The day keeps the default order (biggest first) meanwhile.`;
  if (data) return `${data.rankedLists} of ${data.lists.length} lists ranked · ${trackerLabel(trackerFromEnum(data.provider))}${query.isFetching || query.pending ? ' · updating…' : ''}`;
  return query.isFetching ? 'Ranking the lists…' : 'Pick a campaign to rank its lists.';
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editor: ContainerEditor;
  group: { name: string; color: string };
  ranking: DropGenListRanking;
  query: ListRankingQuery;
  ordered: QueueRow[];
  preview: DropGenListRankingPreview | undefined;
  onChange: (ranking: DropGenListRanking) => void;
}

export const ListFormulaDialog: FC<Props> = ({ open, onOpenChange, ...rest }) => (
  <OptimizerDialog open={open} onOpenChange={onOpenChange}>
    <ListFormulaBody {...rest} onClose={() => onOpenChange(false)} />
  </OptimizerDialog>
);

const ListFormulaBody: FC<Omit<Props, 'open' | 'onOpenChange'> & { onClose: () => void }> = ({ editor, group, ranking, query, ordered, preview, onChange, onClose }) => (
  <OptimizerBody
    title="List formula"
    subtitle={<DialogSubject editor={editor} extra={<GroupChip name={group.name} color={group.color} />} />}
    tabs={[
      { value: 'formula', label: 'Formula', icon: Sigma, content: <HourFormulaCard formula={ranking.formula} onChange={(formula) => onChange({ ...ranking, formula })} hint={HINT} /> },
      { value: 'rules', label: 'Rules', icon: ListChecks, content: <RulesCard surface="lists" rules={ranking.rules} onChange={(rules) => onChange({ ...ranking, rules })} firstRange={ranking.formula.groups[0]} /> },
    ]}
    panel={
      <ListFormulaResults ordered={ordered} preview={preview} query={query} />
    }
    panelFill
    panelWidthClass="lg:w-[46rem]"
    onClose={onClose}
  />
);
